/**
 * Inspecciones que nacen de un tag especial del patio.
 *
 * Dos clases, y no se parecen:
 *
 *  · proveedor — no descarga nada. Lo único que se captura es quién vino, quién
 *    lo recibió y a qué: tres campos, sin fotos ni parámetros de agua.
 *  · pipa sin tag — una descarga normal de una unidad que entró con el tag
 *    compartido, así que antes de inspeccionarla hay que decir CUÁL unidad es
 *    (paso 1, el registro manual) y recién entonces va la inspección de
 *    siempre (paso 2).
 *
 * Módulo puro: `yardInspectionsStore.ts` importa `utils/api` → firebase y no
 * colecta en un worktree sin `apps/v5/.env`. Los tipos se declaran
 * estructurales para que este archivo no dependa de ese store ni en tipos.
 */

export type SpecialInspectionKind = "provider" | "untagged";

export interface SpecialInspectionFields {
  inspection_type?: string | null;
  special_tag?: string | null;
  provider_name?: string | null;
  received_by?: string | null;
  visit_reason?: string | null;
  registration_completed_at?: unknown;
  tag_presence?: string | null;
  tag_position?: string | null;
  card_number?: string | null;
}

/**
 * Qué inspección especial es, o `null` si es una normal.
 *
 * El proveedor se reconoce por `special_tag` y NO por `inspection_type`:
 * `inspection_type === "provider"` ya existía antes de los tags especiales
 * (la unidad con `is_provider: true`) y esas visitas tienen que seguir
 * naciendo "Cancelada" y abriendo el modal de siempre. `special_tag` sólo lo
 * escribe el backend cuando la inspección nació del tag del patio, así que es
 * lo único que separa lo nuevo de la historia.
 */
export function specialInspectionKind(
  inspection: SpecialInspectionFields,
): SpecialInspectionKind | null {
  if (inspection.special_tag === "provider") return "provider";
  if (
    inspection.inspection_type === "untagged" ||
    inspection.special_tag === "untagged"
  ) {
    return "untagged";
  }
  return null;
}

// ─── Proveedor ───────────────────────────────────────────────────────────────

/**
 * Los tres campos del proveedor y su tope. Los topes son los de la lista
 * blanca de `PUT /yardInspection` (200 / 200 / 500): el backend recorta, y un
 * texto que se guarda cortado sin avisar es peor que un input que no deja
 * pasarse.
 */
export const PROVIDER_VISIT_FIELDS = [
  { field: "provider_name", maxLength: 200 },
  { field: "received_by", maxLength: 200 },
  { field: "visit_reason", maxLength: 500 },
] as const;

export type ProviderVisitField = (typeof PROVIDER_VISIT_FIELDS)[number]["field"];
export type ProviderVisitForm = Record<ProviderVisitField, string>;
export type ProviderVisitError = "required" | "tooLong";

const text = (value: unknown): string =>
  typeof value === "string" ? value : "";

export function providerVisitFormFrom(
  inspection: SpecialInspectionFields,
): ProviderVisitForm {
  return {
    provider_name: text(inspection.provider_name),
    received_by: text(inspection.received_by),
    visit_reason: text(inspection.visit_reason),
  };
}

/**
 * Los tres son obligatorios, y "sólo espacios" cuenta como vacío: el backend
 * recorta antes de guardar, así que aceptarlo acá dejaría la visita en
 * "pendiente" justo después de un guardado que dijo que salió bien.
 */
export function validateProviderVisit(
  form: ProviderVisitForm,
): Partial<Record<ProviderVisitField, ProviderVisitError>> {
  const errors: Partial<Record<ProviderVisitField, ProviderVisitError>> = {};
  for (const { field, maxLength } of PROVIDER_VISIT_FIELDS) {
    const value = form[field].trim();
    if (!value) errors[field] = "required";
    else if (value.length > maxLength) errors[field] = "tooLong";
  }
  return errors;
}

/** Lo que viaja en el PUT: recortado, igual que lo va a guardar el backend. */
export function providerVisitPayload(form: ProviderVisitForm): ProviderVisitForm {
  return {
    provider_name: form.provider_name.trim(),
    received_by: form.received_by.trim(),
    visit_reason: form.visit_reason.trim(),
  };
}

export function providerVisitDirty(
  form: ProviderVisitForm,
  inspection: SpecialInspectionFields,
): boolean {
  const saved = providerVisitFormFrom(inspection);
  return PROVIDER_VISIT_FIELDS.some(
    ({ field }) => form[field].trim() !== saved[field].trim(),
  );
}

/** Los tres campos capturados: es lo que separa `provider_complete` de `provider_pending`. */
export function providerVisitComplete(
  inspection: SpecialInspectionFields,
): boolean {
  const form = providerVisitFormFrom(inspection);
  return PROVIDER_VISIT_FIELDS.every(({ field }) => !!form[field].trim());
}

// ─── Pipa sin tag ────────────────────────────────────────────────────────────

/**
 * El paso 1 está hecho cuando el backend lo selló
 * (`PUT /yardInspection/registration` escribe `registration_completed_at`).
 * No se deduce de que haya vehículo o placa: esos campos también los puede
 * llenar el formulario normal, y lo que el paso 1 garantiza —que el evento de
 * entrada quedó actualizado con lo capturado— sólo lo hace ese endpoint.
 */
export function untaggedRegistrationComplete(
  inspection: SpecialInspectionFields,
): boolean {
  return !!inspection.registration_completed_at;
}

/**
 * Las dos preguntas que van ANTES de elegir la unidad: por qué la pluma no la
 * reconoció. O trae tag y no lo lee —y entonces dónde va pegado en el
 * parabrisas—, o no trae. Los valores son los que acepta el backend
 * (specialInspections.js, TAG_PRESENCE_VALUES / TAG_POSITION_VALUES); las
 * posiciones van en el orden en que se ofrecen.
 */
export const TAG_PRESENCE_OPTIONS = ["unreadable", "none"] as const;
export type TagPresence = (typeof TAG_PRESENCE_OPTIONS)[number];

export const TAG_POSITION_OPTIONS = [
  "top_left",
  "bottom_left",
  "top_center",
  "bottom_center",
  "top_right",
  "bottom_right",
] as const;
export type TagPosition = (typeof TAG_POSITION_OPTIONS)[number];

/** `""` = sin contestar, que es como abre el `<select>`. */
export interface TagSurvey {
  tag_presence: TagPresence | "";
  tag_position: TagPosition | "";
}

const oneOf = <T extends string>(
  options: readonly T[],
  value: unknown,
): T | "" => (options.includes(value as T) ? (value as T) : "");

/** Lo ya contestado, para volver al paso 1 a corregir sin preguntarlo de nuevo. */
export function tagSurveyFrom(inspection: SpecialInspectionFields): TagSurvey {
  const tag_presence = oneOf(TAG_PRESENCE_OPTIONS, inspection.tag_presence);
  return {
    tag_presence,
    tag_position:
      tag_presence === "unreadable"
        ? oneOf(TAG_POSITION_OPTIONS, inspection.tag_position)
        : "",
  };
}

/** Si ya se puede mostrar el formulario de la unidad. */
export function tagSurveyComplete(survey: TagSurvey): boolean {
  if (survey.tag_presence === "none") return true;
  return survey.tag_presence === "unreadable" && !!survey.tag_position;
}

/** Lo que viaja en el PUT. "No trae" manda la posición en null, no la que quedó elegida. */
export function tagSurveyPayload(survey: TagSurvey): {
  tag_presence: TagPresence | null;
  tag_position: TagPosition | null;
} {
  const tag_presence = survey.tag_presence || null;
  return {
    tag_presence,
    tag_position:
      tag_presence === "unreadable" ? survey.tag_position || null : null,
  };
}

/**
 * Al dar salida manual: ¿la unidad entró LEÍDA por su propio tag? Mismo
 * criterio que el backend (specialInspections.visitEnteredByTag): tarjeta propia
 * (no un tag especial compartido) Y un evento de entrada que no sea alta manual
 * —una entrada manual también puede traer la tarjeta tecleada—. Sin el origen
 * de la entrada (`entrySource` desconocido) se preguntan las dos cosas: el
 * backend siempre acepta las dos respuestas.
 *
 *  · "position" → entró por su tag: lo trae y falló la lectora de SALIDA, así
 *    que sólo se pregunta dónde va en el parabrisas.
 *  · "full" → entró sin lectura: las mismas dos preguntas que la entrada.
 */
export type ExitTagSurveyMode = "position" | "full";
export function exitTagSurveyMode(
  inspection: SpecialInspectionFields,
  entrySource: string | null | undefined,
): ExitTagSurveyMode {
  const card = (inspection.card_number || "").trim();
  return !inspection.special_tag && card && !!entrySource && entrySource !== "manual"
    ? "position"
    : "full";
}

/** Si ya se puede confirmar la salida manual. */
export function exitTagSurveyComplete(
  mode: ExitTagSurveyMode,
  survey: TagSurvey,
): boolean {
  return mode === "position" ? !!survey.tag_position : tagSurveyComplete(survey);
}

/**
 * Lo que viaja en POST /yardInspection/manualExit. En modo "position" no se
 * manda la presencia: la infiere el backend, y mandarla sería contradecirlo.
 */
export function exitTagSurveyPayload(
  mode: ExitTagSurveyMode,
  survey: TagSurvey,
): { tag_presence?: TagPresence | null; tag_position: TagPosition | null } {
  if (mode === "position") return { tag_position: survey.tag_position || null };
  return tagSurveyPayload(survey);
}

export type RegistrationErrorKey =
  | "specialInspection.untagged.errors.tagSurvey"
  | "specialInspection.untagged.errors.notFound"
  | "specialInspection.untagged.errors.notUntagged"
  | "specialInspection.untagged.errors.unitRequired"
  | "specialInspection.untagged.errors.vehicleNotFound"
  | "specialInspection.untagged.errors.forbidden"
  | "specialInspection.untagged.errors.generic";

/**
 * Error de `PUT /yardInspection/registration` → clave i18n (namespace
 * truckYard). Primero el código que manda el backend en `meta.message` y,
 * si no es uno conocido, el status: un 403 es el recorte por patio y un 404
 * sin código reconocible sigue siendo "ya no existe".
 *
 * `YardInspectionNotFound` es el código que ya usa `PUT /yardInspection`; el
 * contrato del endpoint nuevo dice `InspectionNotFound`. Se aceptan los dos.
 */
export function registrationErrorKey(error: unknown): RegistrationErrorKey {
  const response = (
    error as {
      response?: { status?: number; data?: { meta?: { message?: unknown } } };
    } | null
  )?.response;
  const code =
    typeof response?.data?.meta?.message === "string"
      ? response.data.meta.message.trim()
      : "";
  if (code === "InvalidTagPresence" || code === "InvalidTagPosition") {
    return "specialInspection.untagged.errors.tagSurvey";
  }
  if (code === "NotUntaggedInspection") {
    return "specialInspection.untagged.errors.notUntagged";
  }
  if (code === "VehicleOrPlateRequired") {
    return "specialInspection.untagged.errors.unitRequired";
  }
  if (code === "VehicleNotFound") {
    return "specialInspection.untagged.errors.vehicleNotFound";
  }
  if (code === "InspectionNotFound" || code === "YardInspectionNotFound") {
    return "specialInspection.untagged.errors.notFound";
  }
  if (response?.status === 403) {
    return "specialInspection.untagged.errors.forbidden";
  }
  if (response?.status === 404) {
    return "specialInspection.untagged.errors.notFound";
  }
  return "specialInspection.untagged.errors.generic";
}

export type UntaggedStep = 1 | 2;

/** En qué paso abre el modal: en el 2 si el registro ya está, para no repetirlo. */
export function untaggedInitialStep(
  inspection: SpecialInspectionFields,
): UntaggedStep {
  return untaggedRegistrationComplete(inspection) ? 2 : 1;
}

/**
 * Si se puede pasar al paso pedido. Al 1 siempre —se puede volver a corregir—;
 * al 2 sólo con el registro hecho: la inspección normal sobre una unidad sin
 * identificar es exactamente el estado "Sin vehículo" que el paso 1 resuelve.
 */
export function canOpenUntaggedStep(
  inspection: SpecialInspectionFields,
  step: UntaggedStep,
): boolean {
  return step === 1 || untaggedRegistrationComplete(inspection);
}
